/**
 * settingsTarget — which item's settings are open, as a store the bar writes
 * and the app's settings surface (the Inspector) reads.
 *
 * Same shape as `editRequest`: the selection action bar lives outside every
 * page and the Inspector lives in the app, so a module-level store keeps both
 * sides decoupled. Unlike an edit request it is a STATE, not an event: the
 * surface stays open on its item until it is closed.
 */
import { useSyncExternalStore } from "react";

export interface SettingsTarget {
  readonly gridId: string;
  readonly itemId: string;
}

let current: SettingsTarget | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

const openedHooks = new Set<() => void>();

/** Run `fn` whenever the settings surface opens (the quick-edit card closes
 *  itself this way — one editing surface at a time). */
export function onSettingsOpened(fn: () => void): () => void {
  openedHooks.add(fn);
  return () => openedHooks.delete(fn);
}

/** Open the settings of `itemId` (in `gridId`). */
export function openSettings(gridId: string, itemId: string): void {
  for (const fn of openedHooks) fn();
  if (current?.gridId === gridId && current.itemId === itemId) return;
  current = { gridId, itemId };
  emit();
}

/** Close the settings surface. */
export function closeSettings(): void {
  if (!current) return;
  current = null;
  emit();
}

/** The current target, read outside React. */
export function settingsTarget(): SettingsTarget | null {
  return current;
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/** The item whose settings are open, or null. */
export function useSettingsTarget(): SettingsTarget | null {
  return useSyncExternalStore(
    subscribe,
    () => current,
    () => null,
  );
}

/** Test seam: close without notifying. */
export function resetSettingsTarget(): void {
  current = null;
}
